/**
 * AiDraftingPanel — CC-2d review-first AI drafting surface (ТЗ §13 AI
 * Drafting Bot, Этап A: review-first, никакого auto-execution).
 *
 * Input is a STRUCTURED AI proposal — a single `AiCommand` JSON object or
 * an array of them (the same shape `window.editorRunAiCommand(Batch)`
 * accepts). The panel:
 *
 *   1. SANDBOX PREVIEW — live-parses the input through the SAME fail-closed
 *      boundary the contour uses (`parseAiCommandBatch` →
 *      `translateAiCommandBatch`) WITHOUT dispatching, and renders exactly
 *      which `EditorCommand`s WILL run. A malformed / non-AI-callable
 *      proposal renders its rejection reason instead and Accept stays
 *      disabled (fail-closed at the UI boundary too).
 *   2. REVIEW-FIRST — nothing is dispatched until the user explicitly
 *      presses Accept; Reject discards the proposal (and logs it).
 *   3. ACCEPT — runs the ORIGINAL proposal through
 *      `runAiCommandBatchThroughSafeContour` (schema parse → CC-2a guard →
 *      grouped-undo batch → store.dispatch → audit/history), with a
 *      localized undo label so the whole AI turn is ONE undo step. The
 *      panel itself never dispatches a translated command directly.
 *   4. TRANSCRIPT — accept / reject / failure outcomes (with rejection
 *      reasons) land in the session transcript model.
 */

import { useMemo, type ReactElement } from "react";
import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  AiCommandValidationError,
  NonAiCallableCommandError,
  parseAiCommandBatch,
  translateAiCommandBatch,
  type AiCommand,
} from "@dxf-ai-editor/ai-commands";

import { runAiCommandBatchThroughSafeContour } from "../../ai/aiSafeContour";
// ABB-S4 — the shared single-in-flight latch (INV-INFLIGHT): the panel Accept
// checks it (held as "panel") so it never NESTS into an open bridge / window-seam
// batch. The latch leaf imports NO editorStore (cycle-free).
import { releaseInFlight, tryAcquireInFlight } from "../../agentBridge/inFlightLatch";
import {
  buildAiDispatchContext,
  collectAiEntitySnapshot,
  collectAiInsertIds,
} from "../../ai/aiDispatchContext";
import { useEditorStore } from "../../state/editorStore";
import { useSysvarStore } from "../../dynamicInput/sysvarStore";
import { useT } from "../../i18n";
import {
  useAiPanelTranscriptStore,
  type AiDraftTranscriptEntry,
} from "./aiPanelTranscript";

// ─── proposal preview (pure, no dispatch) ───────────────────────────

type ProposalPreview =
  | { kind: "empty" }
  | { kind: "invalid-json"; reason: string }
  | { kind: "rejected"; reason: string }
  | {
      kind: "ready";
      aiCommands: AiCommand[];
      editorCommands: EditorCommand[];
    };

/** Compact one-line payload summary for a previewed EditorCommand. */
function describeEditorCommand(command: EditorCommand): string {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(command)) {
    // `type` is the row headline; `source` is always the AI system tag.
    if (key !== "type" && key !== "source") payload[key] = value;
  }
  const text = JSON.stringify(payload);
  return text.length > 120 ? `${text.slice(0, 117)}…` : text;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Build the AI dispatch context from the shell state AND stamp the LIVE
 * MIRRTEXT sysvar onto it (Codex #342 F2). `buildAiDispatchContext` is kept
 * store-free (it takes the shell state as a parameter to preserve the acyclic
 * dependency direction), but the live MIRRTEXT lives in `useSysvarStore`, not
 * the shell — so the AI panel (the SHELL-side caller) reads it here and threads
 * it through `AiDispatchContext.mirrtext`. The MIRROR translator stamps this
 * onto the emitted command when the AI proposal omits its own value, so an AI
 * MIRROR honours a live `SETVAR MIRRTEXT 1` exactly like the draft/CLI path.
 */
function buildAiDispatchContextWithSysvars(
  state: Parameters<typeof buildAiDispatchContext>[0],
): ReturnType<typeof buildAiDispatchContext> {
  return {
    ...buildAiDispatchContext(state),
    mirrtext: useSysvarStore.getState().values.MIRRTEXT,
  };
}

/**
 * Parse + translate the proposal text WITHOUT dispatching — the sandbox
 * preview runs the exact fail-closed boundary the contour will apply, so
 * what the user reviews is what the contour will execute.
 */
function buildProposalPreview(
  input: string,
  buildContext: () => ReturnType<typeof buildAiDispatchContext>,
): ProposalPreview {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { kind: "empty" };

  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(trimmed);
  } catch (error) {
    return { kind: "invalid-json", reason: errorMessage(error) };
  }

  const proposals = Array.isArray(parsedJson) ? parsedJson : [parsedJson];
  try {
    const aiCommands = parseAiCommandBatch(proposals);
    const editorCommands = translateAiCommandBatch(
      aiCommands,
      buildContext(),
    );
    return { kind: "ready", aiCommands, editorCommands };
  } catch (error) {
    if (
      error instanceof AiCommandValidationError ||
      error instanceof NonAiCallableCommandError
    ) {
      return { kind: "rejected", reason: error.message };
    }
    // Translator-level fail-closed rejections (e.g. MSPACE with no paper
    // layout to resolve) surface the same way: reason shown, no dispatch.
    return { kind: "rejected", reason: errorMessage(error) };
  }
}

/** Short "INSERT_BLOCK, EXPLODE" actions summary for transcript entries. */
function summarizeActions(aiCommands: readonly AiCommand[]): string {
  return aiCommands.map((command) => command.action).join(", ");
}

// ─── panel ───────────────────────────────────────────────────────────

export function AiDraftingPanel(): ReactElement {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);
  const recordUiClick = useEditorStore((store) => store.recordUiClick);
  // Subscribe to the shell state so the sandbox preview re-translates when
  // the dispatch context (active space / layouts) changes under it.
  const shellState = useEditorStore((store) => store.state);
  const draftInput = useAiPanelTranscriptStore((store) => store.draftInput);
  const setDraftInput = useAiPanelTranscriptStore(
    (store) => store.setDraftInput,
  );
  const draftEntries = useAiPanelTranscriptStore(
    (store) => store.draftEntries,
  );
  const appendDraftEntry = useAiPanelTranscriptStore(
    (store) => store.appendDraftEntry,
  );

  const undoLabel = t("ai.draft.undo_label");

  // Codex #342 F4: subscribe the preview to the LIVE MIRRTEXT sysvar. The
  // builder reads `useSysvarStore.getState().values.MIRRTEXT` imperatively, but
  // a SETVAR mutates the sysvar store (not the shell state), so without this
  // dependency the memo would not re-run after `SETVAR MIRRTEXT 1` — the preview
  // would show `mirrtext:0` while Accept (which re-reads getState() fresh)
  // dispatches `mirrtext:1`. Subscribing here keeps the "what will be
  // dispatched" preview in sync with what Accept actually dispatches.
  const liveMirrtext = useSysvarStore((store) => store.values.MIRRTEXT);

  const preview = useMemo(
    () =>
      buildProposalPreview(draftInput, () =>
        buildAiDispatchContextWithSysvars(shellState),
      ),
    // `liveMirrtext` is an INTENTIONAL dependency: the memo body reads the live
    // MIRRTEXT IMPERATIVELY via `useSysvarStore.getState()` (which the
    // exhaustive-deps rule cannot trace), so the subscription value must be in
    // the deps to re-run the memo when MIRRTEXT changes. eslint flags it as
    // "unnecessary" because it can't see the imperative read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftInput, shellState, liveMirrtext],
  );

  function acceptProposal(): void {
    if (preview.kind !== "ready") return;
    // ABB-S4 — claim the shared single-in-flight latch (held as "panel") BEFORE the
    // contour opens its grouped-undo batch. If the bridge / window-seam holds an
    // open turn, REFUSE rather than nest (a nested begin-batch depth+1 would fold
    // this Accept into the other client's batch, breaking one-undo-per-turn).
    if (!tryAcquireInFlight("panel")) {
      // P3 (round-3) — the proposal is REFUSED here; no commands run. Do NOT emit
      // the "accept" event (it would inflate acceptance telemetry whenever a turn
      // is in flight). Record a DISTINCT busy/refusal event instead so the refusal
      // stays observable, reusing the existing `recordUiClick` (ui-click) vocabulary
      // with a separate controlId + warning tone.
      recordUiClick({
        target: "ai-draft",
        controlId: "accept-refused-busy",
        summary: `AI draft accept refused (turn in flight): ${summarizeActions(preview.aiCommands)}`,
        tone: "warning",
      });
      appendDraftEntry({
        kind: "failed",
        title: t("ai.draft.entry_failed_title"),
        detail: t("ai.draft.entry_failed", {
          reason: t("ai.draft.latch_busy_reason"),
        }),
        tone: "warning",
      });
      return;
    }
    // §14 telemetry — AI usage (non-command ui ring; the accepted commands
    // themselves produce real audit events through the contour dispatch).
    // Emitted ONLY after the latch is acquired, so the "accepted" event tracks a
    // proposal that actually proceeds (never the refused / busy path above).
    recordUiClick({
      target: "ai-draft",
      controlId: "accept",
      summary: `AI draft accepted: ${summarizeActions(preview.aiCommands)}`,
    });
    try {
      const result = runAiCommandBatchThroughSafeContour(
        preview.aiCommands,
        dispatch,
        {
          context: buildAiDispatchContextWithSysvars(
            useEditorStore.getState().state,
          ),
          label: undoLabel,
          resolveInsertIds: () =>
            collectAiInsertIds(useEditorStore.getState().geometryDocument),
          // T2 — surface the turn's CREATED / AFFECTED entity ids on the result.
          snapshotEntities: () =>
            collectAiEntitySnapshot(useEditorStore.getState().geometryDocument),
        },
      );
      appendDraftEntry({
        kind: "accepted",
        title: t("ai.draft.entry_accepted_title"),
        detail: t("ai.draft.entry_accepted", {
          actions: summarizeActions(preview.aiCommands),
          count: result.commands.length,
          label: undoLabel,
        }),
        tone: "success",
      });
      setDraftInput("");
    } catch (error) {
      // Mid-turn dispatch failure — the contour already aborted the batch
      // (document unchanged); surface the reason in the transcript.
      appendDraftEntry({
        kind: "failed",
        title: t("ai.draft.entry_failed_title"),
        detail: t("ai.draft.entry_failed", {
          reason: errorMessage(error),
        }),
        tone: "warning",
      });
    } finally {
      // Always release the latch — even a thrown/aborted turn must free it.
      releaseInFlight("panel");
    }
  }

  function rejectProposal(): void {
    if (preview.kind === "empty") return;
    recordUiClick({
      target: "ai-draft",
      controlId: "reject",
      summary: "AI draft rejected by user",
    });
    appendDraftEntry({
      kind: "rejected",
      title: t("ai.draft.entry_rejected_title"),
      detail:
        preview.kind === "ready"
          ? t("ai.draft.entry_rejected", {
              actions: summarizeActions(preview.aiCommands),
            })
          : t("ai.draft.entry_rejected_reason", { reason: preview.reason }),
      tone: "warning",
    });
    setDraftInput("");
  }

  return (
    <div className="section-subcard" data-testid="ai-draft-panel">
      <div className="section-heading">
        <div>
          <p className="section-label">{t("ai.draft.section_label")}</p>
          <h3>{t("ai.draft.title")}</h3>
        </div>
        <span className="badge">{t("ai.draft.badge")}</span>
      </div>
      <p className="panel-note">{t("ai.draft.intro")}</p>

      <label className="field-stack">
        <span>{t("ai.draft.input_label")}</span>
        <textarea
          className="property-input"
          data-testid="ai-draft-input"
          rows={5}
          value={draftInput}
          placeholder={t("ai.draft.input_placeholder")}
          onChange={(event) => setDraftInput(event.target.value)}
        />
      </label>

      {/* Sandbox preview — what WILL be dispatched on Accept. */}
      <div className="panel-stack" data-testid="ai-draft-preview">
        <p className="section-label">{t("ai.draft.preview_label")}</p>
        {preview.kind === "ready" ? (
          <>
            <p className="panel-note">
              {t("ai.draft.preview_count", {
                count: preview.editorCommands.length,
              })}
            </p>
            <ol
              className="history-list"
              data-testid="ai-draft-preview-list"
            >
              {preview.editorCommands.map((command, index) => (
                <li
                  // Preview rows are positional (a proposal edit rebuilds
                  // the whole list), so the index+type key is stable enough.
                  key={`${index}-${command.type}`}
                  className="history-card"
                >
                  <strong>{command.type}</strong>
                  <p>{describeEditorCommand(command)}</p>
                </li>
              ))}
            </ol>
            <p className="microcopy">
              {t("ai.draft.grouped_undo_note", { label: undoLabel })}
            </p>
          </>
        ) : null}
        {preview.kind === "empty" ? (
          <p className="panel-note">{t("ai.draft.preview_empty")}</p>
        ) : null}
        {preview.kind === "invalid-json" ? (
          <p className="panel-note" data-testid="ai-draft-rejection">
            {t("ai.draft.invalid_json", { message: preview.reason })}
          </p>
        ) : null}
        {preview.kind === "rejected" ? (
          <p className="panel-note" data-testid="ai-draft-rejection">
            {t("ai.draft.rejection_label")}: {preview.reason}
          </p>
        ) : null}
      </div>

      <div className="action-row">
        <button
          className="primary-button"
          type="button"
          data-testid="ai-draft-confirm"
          disabled={preview.kind !== "ready"}
          onClick={acceptProposal}
        >
          {t("ai.draft.accept")}
        </button>
        <button
          className="ghost-button"
          type="button"
          data-testid="ai-draft-reject"
          disabled={preview.kind === "empty"}
          onClick={rejectProposal}
        >
          {t("ai.draft.reject")}
        </button>
      </div>

      {/* Session transcript — accept / reject / failure outcomes. */}
      <p className="section-label">{t("ai.draft.transcript_label")}</p>
      <div className="history-list" data-testid="ai-draft-transcript">
        {draftEntries.length === 0 ? (
          <p className="panel-note">{t("ai.draft.transcript_empty")}</p>
        ) : (
          draftEntries.map((entry: AiDraftTranscriptEntry) => (
            <article
              key={entry.id}
              className={`history-card tone-${entry.tone}`}
            >
              <strong>{entry.title}</strong>
              <p>{entry.detail}</p>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
